import { useWindowSize } from "@react-hookz/web";
import { saveAs } from "file-saver";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, DropdownMenu, Icon, IconButton, Loading, SearchInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator, usePagination } from "@/hooks";
import { useLazyExportDriversQuery } from "@/redux/apis/reporting/reportingApi";
import { addToast, clsx } from "@/utils";
import { useDriversContext } from "../../../context";
import { DriversFilters } from "../../../features/Filters/DriversFilters";
import { useGetFleetDriversParams } from "../hooks/useGetFleetDriversParams";

export const MyDriversActionMenu = () => {
  const { setStep } = useDriversContext();
  const { width } = useWindowSize();
  const operator = useOperator();
  const [exportDrivers, { isFetching }] = useLazyExportDriversQuery();
  const { search, setSearch } = useGetFleetDriversParams();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const { clearPagination } = usePagination("my-drivers");

  const handleSearch = (value?: string) => {
    if (!value) setIsSearchActive(false);

    clearPagination();
    setSearch(value);
  };

  const handleExportDrivers = (type: "full" | "active") => {
    exportDrivers({ report_type: type })
      .unwrap()
      .then((res) => saveAs(res.data.path, "drivers"))
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const handleCopyDriverAppLink = () => {
    const link = operator.registerLinks.driver;
    if (!link) {
      addToast("danger", "Driver App Link is not available.");
      return;
    }
    navigator.clipboard.writeText(link).then(() => {
      addToast("success", "Driver App Link copied. Share it with your driver.");
    });
  };

  if (!isSearchActive && !search) {
    return (
      <div className="flex flex-1 items-center justify-end gap-1 xl:gap-2 ">
        <Button
          className="max-md:[&>span]:hidden"
          variant="tertiary"
          size="sm"
          startIcon="SearchNormal1"
          onClick={() => setIsSearchActive(true)}
        >
          Search
        </Button>
        <FilterButton />
        <DropdownMenu
          button={
            <div className="relative">
              {isFetching && <Loading />}
              {width >= 768 ? (
                <DropdownMenu.Button as={Button} startIcon="Add" isDropdown size="sm" className="w-full">
                  Actions
                </DropdownMenu.Button>
              ) : (
                <DropdownMenu.Button as={IconButton} iconName="options" isCustomIcon variant="primary" size="sm" className="fill-white" />
              )}
            </div>
          }
          isCustomButton
          position="bottom-start"
        >
          <DropdownMenu.Item onClick={() => setStep("invite")} className="w-[298px] text-primary-dark">
            <Icon name="Add" className="mr-2.5" size="sm" />
            <Typography>Invite Driver</Typography>
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={handleCopyDriverAppLink} className="w-[298px] text-primary-dark">
            <Icon name="Copy" className="mr-2.5" size="sm" />
            <Typography>Copy Driver App Link</Typography>
          </DropdownMenu.Item>
          <Link to="../application-rules">
            <DropdownMenu.Item className="w-[298px] text-primary-dark">
              <Icon name="Setting4" className="mr-2.5" size="sm" />
              <Typography>Driver Application Rules</Typography>
            </DropdownMenu.Item>
          </Link>
          <DropdownMenu.Item onClick={() => handleExportDrivers("active")} className="w-[298px] text-primary-dark">
            <Icon name="Export" className="mr-2.5" size="sm" />
            <Typography>Export Drivers (Approved Drivers)</Typography>
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => handleExportDrivers("full")} className="w-[298px] text-primary-dark">
            <Icon name="Export" className="mr-2.5" size="sm" />
            <Typography>Export Drivers (Full)</Typography>
          </DropdownMenu.Item>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-end gap-2">
      <SearchInput
        search={search}
        onSearch={handleSearch}
        autoFocus
        placeholder="Search Drivers"
        className={clsx("w-full max-w-sm p-0 sm:w-fit [&>input]:transition-size", isSearchActive && "[&>input]:w-[280px]")}
        size="sm"
        onClear={() => setIsSearchActive(false)}
      />
    </div>
  );
};

const FilterButton = () => {
  const { width } = useWindowSize();

  return width >= 768 ? <DriversFilters.Button /> : <DriversFilters.DrawerButton />;
};
